import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Contratti di Cash Management (PR B).
//
// Modellato sul contratto REALE (Luce Srl, rif. EW0041), non sul vecchio
// facsimile: N schede economiche allegate invece di tre allegati fissi,
// nessuna percentuale A50/B100, e tre firme sul corpo più una per scheda.
// Il contratto copre N punti vendita, che vivono nelle righe delle schede —
// per questo non c'è nessun `sede_id`.
// ---------------------------------------------------------------------------

export type StatoContratto = "bozza" | "inviato_al_cliente" | "firmato" | "annullato" | "archiviato";

/**
 * I tipi di scheda economica allegabili. **L'elenco non è chiuso**: questi due
 * sono quelli che il contratto reale porta davvero, il resto non è ancora
 * fissato.
 */
export type TipoSchedaEconomica =
  | "servizi_accredito"
  | "hardware_software"
  | "manutenzione_hardware_software";

/**
 * I punti di firma: tre fissi sul corpo più uno per scheda allegata, sempre
 * del Cliente. **EVO firma una volta sola per l'intero contratto** e non
 * controfirma gli allegati.
 */
export type TipoPuntoFirma =
  | "corpo_proposta_cliente"
  | "corpo_vessatorie_cliente"
  | "corpo_evo"
  | "scheda_cliente";

export type ParteFirmataria = "cliente" | "evo";

export interface SchedaEconomica {
  id: string;
  tipo: TipoSchedaEconomica;
  ordine: number;
  note: string | null;
  /** Come si chiama sul documento: la manda il backend. */
  etichetta?: string;
  /** Le righe arrivano insieme alla scheda dal dettaglio del contratto. Solo
      quella del tipo giusto è popolata: le altre due sono elenchi vuoti. */
  righe_accredito?: RigaAccredito[];
  righe_hardware?: RigaHardware[];
  righe_manutenzione?: RigaManutenzione[];
}

export interface PuntoFirma {
  id: string;
  contratto_id: string;
  tipo: TipoPuntoFirma;
  scheda_id: string | null;
  firmato: boolean;
  data_firma: string | null;
  verificato_da: string | null;
  /**
   * Etichetta e parte arrivano dal backend, dallo stesso posto in cui è
   * definita la regola delle firme. `parte` **non è una colonna**: si ricava
   * dal tipo, e ricavarla anche qui sarebbe un secondo posto da cui sbagliarla.
   */
  parte: ParteFirmataria;
  etichetta: string;
  scheda?: { id: string; tipo: TipoSchedaEconomica } | null;
}

export interface Contratto {
  id: string;
  cliente_id: string;
  /** «Riferimento Contratto» in testa al documento, es. `EW0041`. */
  riferimento_contratto: string | null;
  riferimento_commerciale: string | null;
  riferimento_partner: string | null;
  ordine_vendita: string | null;
  data_contratto: string | null;
  preavviso_disdetta_giorni: number;
  template_versione: string | null;
  data_generazione: string;
  stato: StatoContratto;
  file_url_pdf: string | null;
  file_url_pdf_firmato: string | null;
  data_invio: string | null;
  created_at: string;
  cliente?: { id: string; nome: string; partita_iva?: string | null } | null;
  creatore?: { id: string; full_name: string | null; email: string } | null;
  schede?: SchedaEconomica[];
  _count?: { punti_firma: number; schede: number };
}

export interface DettaglioContratto {
  contratto: Contratto;
  schede: SchedaEconomica[];
  punti_firma: PuntoFirma[];
  firmato_ovunque: boolean;
}

export const STATO_LABEL: Record<StatoContratto, string> = {
  bozza: "Bozza",
  inviato_al_cliente: "Inviato al Cliente",
  firmato: "Firmato",
  annullato: "Annullato",
  archiviato: "Archiviato",
};

export const TIPO_SCHEDA_LABEL: Record<TipoSchedaEconomica, string> = {
  servizi_accredito: "Servizi Accredito",
  hardware_software: "Hardware e Software",
  manutenzione_hardware_software: "Manutenzione Hardware e Software",
};

export async function listContratti(params: { cliente_id?: string; stato?: StatoContratto } = {}): Promise<{
  contratti: Contratto[];
}> {
  const { data } = await apiClient.get<{ contratti: Contratto[] }>("/contratti", { params });
  return data;
}

export async function getContratto(id: string): Promise<DettaglioContratto> {
  const { data } = await apiClient.get<DettaglioContratto>(`/contratti/${id}`);
  return data;
}

export async function generaContratto(payload: {
  cliente_id: string;
  riferimento_contratto?: string;
  riferimento_commerciale?: string;
  riferimento_partner?: string;
  ordine_vendita?: string;
  data_contratto?: string;
  /** I tipi di scheda da allegare subito; se ne possono aggiungere dopo. */
  schede?: TipoSchedaEconomica[];
}): Promise<{ contratto: Contratto }> {
  const { data } = await apiClient.post<{ contratto: Contratto }>("/contratti/genera", payload);
  return data;
}

export async function aggiornaContratto(
  id: string,
  payload: {
    riferimento_contratto?: string;
    riferimento_commerciale?: string;
    riferimento_partner?: string;
    ordine_vendita?: string;
  }
): Promise<{ contratto: Contratto }> {
  const { data } = await apiClient.patch<{ contratto: Contratto }>(`/contratti/${id}`, payload);
  return data;
}

/** Allega una scheda economica a un contratto in bozza, con la sua firma. */
export async function aggiungiScheda(
  contrattoId: string,
  tipo: TipoSchedaEconomica,
  note?: string
): Promise<{ scheda: SchedaEconomica }> {
  const { data } = await apiClient.post<{ scheda: SchedaEconomica }>(`/contratti/${contrattoId}/schede`, {
    tipo,
    note,
  });
  return data;
}

export async function rimuoviScheda(schedaId: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/schede-economiche/${schedaId}`);
  return data;
}

/**
 * Come il contratto arriva al Cliente.
 *
 * `email` parte da noi via Microsoft Graph. `odoo` allega il PDF all'ordine di
 * vendita e lascia spedire a Odoo: il documento resta dov'è già il resto della
 * trattativa, e l'email parte dal sistema che il Cliente riconosce.
 */
export type CanaleInvio = "email" | "odoo";

export interface EsitoInvio {
  contratto: Contratto;
  /**
   * `false` quando Odoo ha solo ALLEGATO senza spedire — succede se non è
   * stato scelto quale template email usare. In quel caso lo stato del
   * contratto NON avanza: il Cliente non ha ricevuto niente.
   */
  spedito: boolean;
  messaggio?: string;
}

/** Spedisce il contratto. Il backend rifiuta se il PDF non c'è ancora. */
export async function inviaContratto(
  id: string,
  opzioni: { canale?: CanaleInvio; destinatario?: string } = {}
): Promise<EsitoInvio> {
  const { data } = await apiClient.post<EsitoInvio>(`/contratti/${id}/invia`, {
    canale: opzioni.canale ?? "email",
    destinatario: opzioni.destinatario,
  });
  return data;
}

/**
 * Genera il PDF del contratto, o lo rigenera.
 *
 * È l'UNICO modo in cui `file_url_pdf` si valorizza: nessun'altra rotta lo
 * scrive, e finché è vuoto il contratto non si può né scaricare né spedire —
 * la `/invia` lo pretende.
 *
 * **Solo su una bozza.** Un contratto già spedito che si rigenera produrrebbe
 * un file diverso da quello che il Cliente ha in mano, con lo stesso nome e
 * nessuno che se ne accorge: il backend risponde 409 e fa bene. Finché è
 * bozza invece si può richiamare quante volte serve — dopo aver aggiunto una
 * scheda economica il documento va rifatto.
 */
export async function generaPdfContratto(id: string): Promise<{ contratto: Contratto }> {
  const { data } = await apiClient.post<{ contratto: Contratto }>(`/contratti/${id}/pdf`);
  return data;
}

export async function caricaContrattoFirmato(id: string, file: File): Promise<{ contratto: Contratto }> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<{ contratto: Contratto }>(`/contratti/${id}/firmato`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function annullaContratto(id: string, motivo?: string): Promise<{ contratto: Contratto }> {
  const { data } = await apiClient.post<{ contratto: Contratto }>(`/contratti/${id}/annulla`, { motivo });
  return data;
}

export async function eliminaContratto(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/contratti/${id}`);
  return data;
}

/**
 * Spunta o toglie una singola firma. Restituisce anche il contratto quando lo
 * stato è cambiato (`null` se è rimasto dov'era).
 */
export async function segnaFirma(
  puntoId: string,
  firmato: boolean,
  dataFirma?: string
): Promise<{ punto_firma: PuntoFirma; contratto: Contratto | null }> {
  const { data } = await apiClient.patch(`/punti-firma/${puntoId}`, {
    firmato,
    data_firma: dataFirma || undefined,
  });
  return data;
}

// ---------------------------------------------------------------------------
// LE RIGHE DELLE SCHEDE ECONOMICHE
//
// Tre forme diverse, come a database e per la stessa ragione: le tre schede
// hanno colonne diverse e nessuna è un sottoinsieme dell'altra. Un tipo unico
// con tutto nullable direbbe che una riga di accredito può avere una marca.
//
// Gli importi arrivano come STRINGA: Prisma espone i `Decimal` come oggetti
// che in JSON diventano stringhe. Dichiararli `number` sarebbe dire il falso —
// è già successo con `lat`/`lng` di `/api/schedule`.
// ---------------------------------------------------------------------------

export type ProvenienzaApparato = "esistente" | "nuova_fornitura_evo" | "nuova_fornitura_cliente";
export type AcquistoOLocazione = "acquisto" | "locazione";
export type VarianteManutenzione = "a_chiamata" | "canone_annuale";
export type UnitaPrezzoManutenzione = "intervento" | "anno";

export interface RigaAccredito {
  id: string;
  scheda_id: string;
  ordine: number;
  sede_id: string | null;
  punto_vendita: string;
  indirizzo: string | null;
  tipo_servizio: string | null;
  durata_mesi: number | null;
  canone_mensile: string | null;
  percentuale_transato: string | null;
  accredito_su_hw_proprio: boolean;
  iban_versamento: string | null;
}

export interface RigaHardware {
  id: string;
  scheda_id: string;
  ordine: number;
  sede_id: string | null;
  punto_vendita: string;
  comune: string | null;
  provenienza: ProvenienzaApparato;
  dispositivo_catalogo_id: string | null;
  marca: string | null;
  modello: string | null;
  attivazione_servizio: string | null;
  acquisto_o_locazione: AcquistoOLocazione;
  azienda_locatrice: string | null;
  garanzia_evo: boolean;
  garanzia_mesi: number | null;
  prezzo_unitario: string | null;
  quantita: number;
}

export interface RigaManutenzione {
  id: string;
  scheda_id: string;
  ordine: number;
  voce_listino_id: string | null;
  variante: VarianteManutenzione;
  tipo_contratto: string;
  nome: string;
  descrizione: string | null;
  copertura_oraria: string | null;
  sla: string | null;
  ricambi: string | null;
  obbligatoria: boolean | null;
  prezzo_applicato: string | null;
  prezzo_nota: string | null;
  unita: UnitaPrezzoManutenzione;
}

export interface VoceListino {
  id: string;
  chiave: string;
  variante: VarianteManutenzione;
  tipo_contratto: string;
  nome: string;
  descrizione: string | null;
  copertura_oraria: string | null;
  sla: string | null;
  ricambi: string | null;
  obbligatoria: boolean | null;
  prezzo: string | null;
  prezzo_nota: string | null;
  unita: UnitaPrezzoManutenzione;
  ordine: number;
  attivo: boolean;
}

export const PROVENIENZA_LABEL: Record<ProvenienzaApparato, string> = {
  esistente: "Esistente",
  nuova_fornitura_evo: "Nuova fornitura Evo",
  nuova_fornitura_cliente: "Nuova fornitura a cura Cliente",
};

export const ACQUISTO_LABEL: Record<AcquistoOLocazione, string> = {
  acquisto: "Acquisto",
  locazione: "Locazione",
};

export const VARIANTE_LABEL: Record<VarianteManutenzione, string> = {
  a_chiamata: "A chiamata",
  canone_annuale: "A canone annuale unitario",
};

/**
 * Aggiunge una riga alla scheda. **Il tipo non si manda**: lo decide la
 * scheda, lato backend — così non esiste il modo di attaccare una riga
 * hardware a una scheda di accredito.
 */
export async function aggiungiRiga(
  schedaId: string,
  payload: Record<string, unknown>
): Promise<{ riga: RigaAccredito | RigaHardware | RigaManutenzione }> {
  const { data } = await apiClient.post(`/schede-economiche/${schedaId}/righe`, payload);
  return data;
}

/** Il segmento di percorso per ciascun tipo di riga. */
const PERCORSO_RIGHE: Record<TipoSchedaEconomica, string> = {
  servizi_accredito: "righe-accredito",
  hardware_software: "righe-hardware",
  manutenzione_hardware_software: "righe-manutenzione",
};

/**
 * PATCH è PARZIALE: i campi non mandati restano quelli di prima. Vale la pena
 * ricordarlo qui perché la prima versione del backend riscriveva la riga
 * intera e svuotava tutto il resto — IBAN compreso.
 */
export async function aggiornaRiga(
  tipo: TipoSchedaEconomica,
  rigaId: string,
  payload: Record<string, unknown>
): Promise<{ riga: RigaAccredito | RigaHardware | RigaManutenzione }> {
  const { data } = await apiClient.patch(`/${PERCORSO_RIGHE[tipo]}/${rigaId}`, payload);
  return data;
}

export async function rimuoviRiga(tipo: TipoSchedaEconomica, rigaId: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/${PERCORSO_RIGHE[tipo]}/${rigaId}`);
  return data;
}

/** Le voci fra cui scegliere per una scheda di manutenzione. */
export async function listinoManutenzione(tutte = false): Promise<{ voci: VoceListino[] }> {
  const { data } = await apiClient.get<{ voci: VoceListino[] }>("/listino-manutenzione", {
    params: tutte ? { tutte: 1 } : {},
  });
  return data;
}
